/*
 * File: DriveNotes.tsx
 * Author: Austin Robinson
 * Date Created: 12/07/2025
 * Copyright (c) DrivingDoc 2025
 */
import { ReactNode } from 'react';

import DescriptionIcon from '@mui/icons-material/Description';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

interface DriveNotesProps {
  /**
   * The content to display inside the notes section.
   * Typically a RichTextViewer component.
   */
  children: ReactNode;
}

/**
 * A styled wrapper component for displaying drive notes.
 * Mimics the BasicRichTextEditor appearance with a header and content area.
 *
 * @example
 * <DriveNotes>
 *   <RichTextViewer value={notes} />
 * </DriveNotes>
 */
export default function DriveNotes({ children }: DriveNotesProps) {
  return (
    <Box mt={1}>
      {/* Notes Header - mimics BasicRichTextEditor toolbar */}
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px 8px 0 0',
          p: 1,
          backgroundColor: 'grey.50',
        }}
      >
        <DescriptionIcon sx={{ fontSize: 16 }} color="action" />
        <Typography variant="caption" fontWeight="medium" color="text.secondary">
          Notes
        </Typography>
      </Box>

      {/* Notes Content - mimics BasicRichTextEditor content area */}
      <Paper
        variant="outlined"
        sx={{
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          p: 1,
          fontSize: '0.75rem',
          color: 'text.secondary',
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}
